import type {
  GlossaryEntry,
  GlossaryRange,
  LibraryContext,
  ReaderContent,
  StoryPage,
  StoryProgress,
  TextSegment,
  TimedWord
} from './types'

export const positiveId = (value: unknown): string | undefined =>
  typeof value === 'string' && /^[1-9]\d*$/.test(value) ? value : undefined

export function libraryContext(query: Record<string, unknown>): LibraryContext {
  const context: LibraryContext = {
    tab: query.tab === 'saved' || query.tab === 'in_progress' ? query.tab : 'all'
  }
  const q = typeof query.q === 'string' ? query.q.trim() : ''
  if (q) context.q = q
  const language = positiveId(query.languageId)
  const level = positiveId(query.levelId)
  if (language) context.languageId = language
  if (level) context.levelId = level
  return context
}
export const libraryQuery = (
  context: Partial<LibraryContext>
): Record<string, string> => ({ ...libraryContext(context) })
export const courseStoriesLink = (course?: {
  language?: { id?: string } | null
  level?: { id?: string } | null
}) => ({
  name: 'stories-library',
  query: libraryQuery({
    languageId: course?.language?.id,
    levelId: course?.level?.id
  })
})

export function nextStoryPage(page: StoryPage): number | undefined {
  if (!page.data?.length) return
  const meta = page.meta
  if (
    !meta ||
    !Number.isInteger(meta.next_page) ||
    !meta.next_page ||
    meta.next_page <= (meta.current_page ?? 0)
  )
    return
  if (meta.last_page !== undefined && meta.next_page > meta.last_page) return
  return meta.next_page
}

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
const array = (value: unknown): unknown[] => (Array.isArray(value) ? value : [])
const integer = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0
export const finiteMs = (value: number) =>
  Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0
export const clampPosition = (value: number, duration: number) =>
  duration > 0 ? Math.min(finiteMs(value), finiteMs(duration)) : finiteMs(value)

export function normalizeContent(raw: unknown): ReaderContent {
  const source = record(raw)
  const canonical = record(source.story).paragraphs
  const paragraphs = Array.isArray(canonical)
    ? canonical.flatMap((text, index) =>
        typeof text === 'string' ? [{ index, text }] : []
      )
    : array(source.paragraphs)
        .flatMap((value) => {
          const p = record(value)
          return integer(p.index) && typeof p.text === 'string'
            ? [{ index: p.index, text: p.text }]
            : []
        })
        .sort((a, b) => a.index - b.index)
  const texts = new Map(paragraphs.map((p) => [p.index, p.text]))
  const words: TimedWord[] = []
  for (const [ordinal, value] of array(
    record(source.timing).words ?? source.timedWords
  ).entries()) {
    const word = record(value)
    if (
      !integer(word.paragraphIndex) ||
      !integer(word.startMs) ||
      !integer(word.endMs) ||
      word.endMs <= word.startMs
    )
      continue
    const text = texts.get(word.paragraphIndex)
    if (text === undefined) continue
    let start = word.charStart
    let end = word.charEnd
    if (
      start == null &&
      end == null &&
      integer(word.wordIndex) &&
      typeof word.text === 'string'
    ) {
      const token = [...text.matchAll(/\S+/gu)][word.wordIndex]
      // Only use the referenced token, permitting punctuation around a unique word.
      // Never guess by first occurrence elsewhere in the paragraph or inside another word.
      if (!token || !word.text) continue
      const offset = token[0].indexOf(word.text)
      if (
        offset < 0 ||
        offset !== token[0].lastIndexOf(word.text) ||
        /[\p{L}\p{N}\p{M}_]/u.test(
          token[0].slice(0, offset) + token[0].slice(offset + word.text.length)
        )
      )
        continue
      const tokenStart = token.index! + offset
      start = tokenStart
      end = tokenStart + word.text.length
    }
    if (!integer(start) || !integer(end) || end <= start || end > text.length)
      continue
    // Do not split a UTF-16 surrogate pair even if a malformed manifest asks us to.
    const splitsPair = (offset: number) =>
      offset > 0 &&
      offset < text.length &&
      /[\uD800-\uDBFF]/.test(text[offset - 1]) &&
      /[\uDC00-\uDFFF]/.test(text[offset])
    if (splitsPair(start) || splitsPair(end)) continue
    words.push({
      index: integer(word.index) ? word.index : ordinal,
      paragraphIndex: word.paragraphIndex,
      text: text.slice(start, end),
      charStart: start,
      charEnd: end,
      startMs: word.startMs,
      endMs: word.endMs
    })
  }
  words.sort((a, b) => a.startMs - b.startMs || a.index - b.index)
  const glossary = array(source.glossary).flatMap((value) => {
    const entry = record(value)
    return typeof entry.term === 'string' &&
      entry.term.trim() &&
      typeof entry.definition === 'string' &&
      entry.definition.trim()
      ? [{ term: entry.term.trim(), definition: entry.definition }]
      : []
  })
  const questions = array(source.comprehensionQuestions ?? source.questions)
    .flatMap((value, ordinal) => {
      const question = record(value)
      const text = question.question ?? question.prompt
      return typeof text === 'string' && text.trim()
        ? [
            {
              index: integer(question.index) ? question.index : ordinal + 1,
              question: text
            }
          ]
        : []
    })
    .sort((a, b) => a.index - b.index)
  return { paragraphs, words, glossary, questions }
}

export function activeWordAt(words: TimedWord[], positionMs: number) {
  // Adapter sorting determines ties; a gap intentionally has no active word.
  for (const word of words) {
    if (word.startMs > positionMs) break
    if (positionMs < word.endMs) return word
  }
}

export function glossaryRanges(
  text: string,
  glossary: GlossaryEntry[]
): GlossaryRange[] {
  const ranges: GlossaryRange[] = []
  for (const entry of glossary) {
    const escaped = entry.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const expression = new RegExp(
      `(?<![\\p{L}\\p{N}\\p{M}_])${escaped}(?![\\p{L}\\p{N}\\p{M}_])`,
      'giu'
    )
    for (const match of text.matchAll(expression))
      ranges.push({
        start: match.index!,
        end: match.index! + match[0].length,
        definition: entry.definition
      })
  }
  return ranges.sort((a, b) => a.start - b.start || b.end - a.end)
}

export function textSegments(
  text: string,
  ranges: GlossaryRange[],
  active?: Pick<TimedWord, 'charStart' | 'charEnd'>,
  words: TimedWord[] = []
): TextSegment[] {
  const boundaries = new Set([0, text.length])
  for (const word of words) {
    boundaries.add(word.charStart)
    boundaries.add(word.charEnd)
  }
  for (const range of ranges) {
    boundaries.add(range.start)
    boundaries.add(range.end)
  }
  if (active) {
    boundaries.add(active.charStart)
    boundaries.add(active.charEnd)
  }
  const offsets = [...boundaries].sort((a, b) => a - b)
  return offsets.slice(0, -1).map((start, i) => {
    const end = offsets[i + 1]
    const glossary = ranges.find((range) => range.start <= start && range.end >= end)
    return {
      start,
      end,
      text: text.slice(start, end),
      active: Boolean(active && active.charStart <= start && active.charEnd >= end),
      glossary: Boolean(glossary),
      definition: glossary?.definition
    }
  })
}

export function mergeProgress(
  previous: StoryProgress | null | undefined,
  incoming: StoryProgress
): StoryProgress {
  const finished =
    previous?.readingStatus === 'finished' || incoming.readingStatus === 'finished'
  return {
    ...previous,
    ...incoming,
    readingStatus: finished
      ? 'finished'
      : (incoming.readingStatus ?? previous?.readingStatus ?? null),
    maxPositionMs: Math.max(
      finiteMs(previous?.maxPositionMs ?? 0),
      finiteMs(incoming.maxPositionMs)
    ),
    progressPercent: finished
      ? 100
      : Math.min(
          100,
          Math.max(previous?.progressPercent ?? 0, incoming.progressPercent || 0)
        )
  }
}
export function storyTime(ms: number): string {
  const seconds = Math.floor(finiteMs(ms) / 1000)
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}
